/* eslint-disable no-console */
import moment from 'moment';
import { useCallback, useEffect, useRef } from 'react';

import { useUserPrint } from '@app/hooks';
import { integrationActionLog } from '@app/services/__global__/integrationActionLog';
import {
  createWSConnection,
  invokeWSMethod,
  subscribeToWsEvent,
  unsubscribeFromWsEvent,
} from '@app/utils/createWSConnection';

type WsEnvelope = {
  isSuccess: boolean;
  jsonData: string;
  message?: string | null;
};

type SocialIntegrationsStatusPing = {
  CountErrors: number;
  DatabaseId: string;
  ErrorIntegrations: [];
  ForUserId: number;
  HasErrors: boolean;
  Timestamp: string;
};

export type UseSocialIntegrationsWsOptions = {
  onChange: (payload: {
    hasErrors: boolean;
    timestamp: string;
    count: number;
  }) => void;
};

const EVENT_DEFAULT = 'OnSocialIntegrationsStatus';

export const useSocialIntegrationsWs = ({
  onChange,
}: UseSocialIntegrationsWsOptions) => {
  const { database } = useUserPrint();

  const mountedRef = useRef(false);

  const handleWsEvent = useCallback(
    (ws: WsEnvelope) => {
      try {
        const raw = ws?.jsonData ?? '{}';
        const parsed = JSON.parse(raw) as Partial<SocialIntegrationsStatusPing>;

        const nextHasErrors = Boolean(parsed?.HasErrors);
        const nextCount = Number(parsed?.CountErrors ?? 0);

        const data = {
          hasErrors: nextHasErrors,
          count: nextCount,
          timestamp: parsed?.Timestamp ?? moment().toISOString(),
        };

        onChange(data);
        // Пока открыт реестр интеграций, смены состояния тоже идут в журнал:
        // без них в выгрузке не видно, что происходило между действиями
        // пользователя (us/11024).
        integrationActionLog.recordStatusChange(parsed);

        localStorage.setItem('socialIntegrationsStatus', JSON.stringify(data));
      } catch (err) {
        console.error('[WS] SocialIntegrationsWarning parse error:', err);
      }
    },
    [onChange]
  );

  const subscribe = useCallback(async () => {
    await createWSConnection(
      async () => {
        try {
          await invokeWSMethod('ListenOrganization', database);
          subscribeToWsEvent(EVENT_DEFAULT, (ws: WsEnvelope) =>
            handleWsEvent(ws)
          );
        } catch (err) {
          console.error('[WS] SocialIntegrationsWarning subscribe error:', err);
        }
      },
      (err) => {
        console.error('[WS] SocialIntegrationsWarning connection error:', err);
      }
    );
  }, [database, handleWsEvent]);

  useEffect(() => {
    mountedRef.current = true;

    subscribe();

    return () => {
      mountedRef.current = false;
      unsubscribeFromWsEvent(EVENT_DEFAULT);
    };
  }, [subscribe]);
};
